import * as React from "react";
import { 
  Plus, 
  History,
  Save,
  RefreshCw,
  AlertCircle,
  Trash2
} from "lucide-react";
import PromptsDropdown from "./PromptsDropdown";
import ActiveExecutionTemplateSelect from "./ActiveExecutionTemplateSelect";
import { StorageService, SaveState } from "../../../../shared/services/storageService";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface ChatToolbarProps {
  onNewChat: () => void;
  onShowHistory: () => void;
  showingHistory?: boolean;
  hideHistory?: () => void;
  conversationHistory?: any[];
  loadConversation?: (id: string) => void;
  currentConversationId?: string | null;
  workpillId?: string;
  executionId?: string | null;
  onDeleteChat?: (id: string, event: React.MouseEvent) => void;
}

const ChatToolbar: React.FC<ChatToolbarProps> = ({ 
  onNewChat, 
  onShowHistory,
  showingHistory,
  hideHistory,
  conversationHistory = [],
  loadConversation,
  currentConversationId,
  workpillId,
  executionId,
  onDeleteChat,
}) => {
  const [saveState, setSaveState] = React.useState<SaveState>({
    isSaving: false,
    lastSavedAt: null,
    error: null
  });
  const [timeAgo, setTimeAgo] = React.useState<string>("");

  // Subscribe to storage state
  React.useEffect(() => {
    return StorageService.subscribe(setSaveState);
  }, []);

  // Update "time ago" every minute
  React.useEffect(() => {
    const updateTimeAgo = () => {
      if (!saveState.lastSavedAt) {
        setTimeAgo("");
        return;
      }
      
      const diff = Math.floor((new Date().getTime() - saveState.lastSavedAt.getTime()) / 1000);
      
      if (diff < 60) {
        setTimeAgo("Saved now");
      } else if (diff < 3600) {
        setTimeAgo(`${Math.floor(diff / 60)}min ago`);
      } else {
        setTimeAgo(`Saved at ${saveState.lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
      }
    };

    updateTimeAgo();
    const interval = setInterval(updateTimeAgo, 60000);
    return () => clearInterval(interval);
  }, [saveState.lastSavedAt]);
  
  const handleSave = async () => {
    await StorageService.manualSave();
  };

  // Close history dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showingHistory && hideHistory) {
        const target = event.target as Element;
        if (!target.closest('[data-history-dropdown]') && !target.closest('[data-history-button]')) {
          hideHistory();
        }
      }
    };

    if (showingHistory) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [showingHistory, hideHistory]);

  return (
    <div className="px-4 py-2 flex justify-between items-center gap-2">
      <div className="min-w-0 flex-1 flex items-center gap-1.5">
        <ActiveExecutionTemplateSelect executionId={executionId} />
        <Button
          variant="ghost"
          size="icon"
          onClick={handleSave}
          disabled={saveState.isSaving}
          className={cn(
            "h-8 w-8 shrink-0 rounded-md",
            saveState.error ? "text-red-500 hover:text-red-600 hover:bg-red-50" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
          )}
          title={saveState.error || (timeAgo || "Save document")}
        >
          {saveState.isSaving ? (
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          ) : saveState.error ? (
            <AlertCircle className="w-3.5 h-3.5" />
          ) : (
            <Save className="w-3.5 h-3.5" />
          )}
        </Button>
      </div>
      {/* Floating toolbar container */}
      <div className="relative flex gap-0.5 items-center bg-white rounded-xl shadow-sm border border-gray-100 px-1.5 py-1 shrink-0">
        <PromptsDropdown workpillId={workpillId} />

        <div className="h-3.5 w-px bg-slate-200 mx-0.5" />

        {/* New Chat Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onNewChat}
          className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 w-7 h-7 rounded-md"
          title="New Chat"
        >
          <Plus className="w-3.5 h-3.5" />
        </Button>
        
        {/* History Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onShowHistory}
          data-history-button
          className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 relative w-7 h-7 rounded-md"
          title={`History (${conversationHistory.length})`}
        >
          <History className="w-3.5 h-3.5" />
          {conversationHistory.length > 0 && (
            <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-gray-600 ring-1 ring-white" />
          )}
        </Button>
        
        {/* History Dropdown */}
        {showingHistory && hideHistory && loadConversation && (
          <div 
            data-history-dropdown 
            className="absolute top-full right-0 w-[280px] bg-white border border-gray-100 rounded-md shadow-lg z-50 max-h-[400px] overflow-y-auto mt-2"
          >
            <div className="p-3 border-b border-gray-100 bg-white/80 backdrop-blur-sm flex justify-between items-center sticky top-0 z-10 rounded-t-xl">
              <span className="text-xs font-semibold text-gray-900">
                History ({conversationHistory.length})
              </span>
              <Button 
                variant="ghost"
                size="sm"
                onClick={hideHistory}
                className="h-6 w-6 p-0 hover:bg-gray-100 text-gray-500 rounded-md"
              >
                ×
              </Button>
            </div>
            {conversationHistory.length === 0 ? (
              <div className="p-6 text-gray-400 text-center text-[13px]">
                No conversations
              </div>
            ) : (
              <div className="py-1">
                {conversationHistory.map((conv, index) => {
                  const isActive = conv.id === currentConversationId;
                  const date = new Date(conv.updated_at);
                  const isToday = date.toDateString() === new Date().toDateString();
                  const dateText = isToday ? 
                    date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false }) :
                    date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
                  
                  const MAX_PREVIEW_LENGTH = 50;
                  const previewText = conv.first_user_message 
                    ? conv.first_user_message.substring(0, MAX_PREVIEW_LENGTH).trim() + (conv.first_user_message.length > MAX_PREVIEW_LENGTH ? '...' : '')
                    : `Chat ${index + 1}`;
                  
                  return (
                    <div
                      key={conv.id}
                      onClick={() => {
                        loadConversation(conv.id);
                        hideHistory();
                      }}
                      className={cn(
                        "mx-1.5 my-0.5 p-2.5 cursor-pointer rounded-md transition-all hover:bg-gray-50 group relative",
                        isActive ? "bg-gray-50 hover:bg-gray-100" : "bg-transparent"
                      )}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <span className={cn(
                          "text-xs font-medium truncate flex-1 mr-2",
                          isActive ? "text-gray-700" : "text-gray-900"
                        )}>
                          {previewText}
                        </span>
                        <span className="text-[10px] text-gray-400 flex-shrink-0">{dateText}</span>
                      </div>
                      {/* Delete button (not available for active chat) */}
                      <div className="flex items-center">
                        {!isActive && (
                          <Button
                            variant="ghost" 
                            size="icon"
                            className="h-6 w-6 ml-auto text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onDeleteChat) onDeleteChat(conv.id, e);
                            }}
                            title="Delete conversation"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatToolbar;

